import { useEffect, useState } from 'react'
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  Link,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { authService } from '../services'
import { getApiErrorMessage } from '../services/apiClient'
import AdminPasswordRequirements from '../components/AdminPasswordRequirements'
import {
  getAdminPasswordPolicyError,
  isAdminPasswordValid,
} from '../utils/adminPasswordPolicy'

function useOpaqueTokenFromUrl() {
  const [searchParams] = useSearchParams()
  const [token, setToken] = useState(null)

  useEffect(() => {
    const t = searchParams.get('token')
    if (t) {
      setToken(t)
      // Strip token from visible URL immediately
      const url = new URL(window.location.href)
      url.searchParams.delete('token')
      window.history.replaceState({}, '', url.pathname + url.search)
    }
  }, [searchParams])

  return token
}

const tokenMessages = {
  expired: 'This password reset link has expired. Please request a new reset link.',
  used: 'This password reset link has already been used. Please log in.',
  superseded: 'This password reset link is no longer valid. Please request a new reset link.',
  deactivated: 'This link is invalid or no longer available.',
  invalid: 'This link is invalid or no longer available.',
}

const ResetPasswordPage = () => {
  const navigate = useNavigate()
  const token = useOpaqueTokenFromUrl()
  const [status, setStatus] = useState('loading')
  const [message, setMessage] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (token === null) return
    if (!token) {
      setStatus('invalid')
      setMessage(tokenMessages.invalid)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        const res = await authService.validateReset(token)
        if (cancelled) return
        if (res.status === 'valid') {
          setStatus('valid')
        } else {
          setStatus(res.status || 'invalid')
          setMessage(tokenMessages[res.status] || res.message || tokenMessages.invalid)
        }
      } catch {
        if (!cancelled) {
          setStatus('invalid')
          setMessage(tokenMessages.invalid)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token])

  useEffect(() => {
    if (status !== 'loading' && status !== 'valid') {
      const t = setTimeout(() => navigate('/login', { replace: true }), 4000)
      return () => clearTimeout(t)
    }
  }, [status, navigate])

  const canSubmit =
    isAdminPasswordValid(password) && password === confirm && !submitting

  const onSubmit = async (e) => {
    e.preventDefault()
    setError('')
    const policyError = getAdminPasswordPolicyError(password)
    if (policyError) {
      setError(policyError)
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match.')
      return
    }
    setSubmitting(true)
    try {
      await authService.resetPassword(token, password)
      navigate('/login', { replace: true })
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not reset password.'))
      setSubmitting(false)
    }
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        p: 2,
      }}
    >
      <Container maxWidth="sm">
        <Card sx={{ bgcolor: 'rgba(15,18,28,0.85)', color: '#fff' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Reset password
            </Typography>
            {status === 'loading' || token === null ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CircularProgress sx={{ color: '#fff' }} />
              </Box>
            ) : status !== 'valid' ? (
              <>
                <Alert severity="error" sx={{ mb: 2 }}>
                  {message}
                </Alert>
                <Typography>
                  <Link component={RouterLink} to="/login" sx={{ color: '#90caf9' }}>
                    Return to sign in
                  </Link>
                </Typography>
              </>
            ) : (
              <Box component="form" onSubmit={onSubmit}>
                {error ? (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    {error}
                  </Alert>
                ) : null}
                <TextField
                  fullWidth
                  required
                  type={showPassword ? 'text' : 'password'}
                  label="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  sx={{ mb: 1, input: { color: '#fff' }, label: { color: 'rgba(255,255,255,0.7)' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          onClick={() => setShowPassword((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <AdminPasswordRequirements password={password} dark />
                <TextField
                  fullWidth
                  required
                  type={showConfirm ? 'text' : 'password'}
                  label="Confirm password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  sx={{ mb: 2, input: { color: '#fff' }, label: { color: 'rgba(255,255,255,0.7)' } }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showConfirm ? 'Hide confirm password' : 'Show confirm password'
                          }
                          onClick={() => setShowConfirm((v) => !v)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          sx={{ color: 'rgba(255,255,255,0.7)' }}
                        >
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" fullWidth variant="contained" disabled={!canSubmit}>
                  Update password
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      </Container>
    </Box>
  )
}

export default ResetPasswordPage
